import * as THREE from 'three';
import { appState } from './stateManager';
import { UnitsUtils } from 'geo-three';
import { MARKER_POS_Y } from './scene.details';

import '../css/scene.css';

export default class Markers {
	constructor(scene, markersReady) {
		this.scene = scene;
		this.markersReady = markersReady;
		this.instancedMesh = null;
		this.pins = [];
	}

	init() {
		appState.subscribe(this.onStateChange.bind(this));
		console.log('markers init');
	}

	getMarkers() {
		return this.pins;
	}

	setup(data) {
		const markerData = {};
		for (const item of data) {
			if (item == null) continue;
			const id = item.id;
			const placeName = item['A település mai neve'] || 'N/A';

			if (id == null) continue;
			if (markerData[id]) continue;
			if (item.lat == null || item.lng == null) continue;
			markerData[id] = {
				id,
				placeName,
				lat: item.lat,
				lng: item.lng
			};
		}

		const markerItems = Object.values(markerData);
		const instanceCount = markerItems.length;

		this.pins = [];

		const height = 520;
		const radius = 180;
		const radialSegments = 4;
		const geometry = new THREE.ConeGeometry(radius, height, radialSegments);
		geometry.computeVertexNormals();

		//coloring gradient
		const pos = geometry.attributes.position;
		const count = pos.count;
		const colors = new Float32Array(count * 3);

		let minY = Infinity;
		let maxY = -Infinity;

		for (let i = 0; i < count; i++) {
			const y = pos.getY(i);
			if (y < minY) minY = y;
			if (y > maxY) maxY = y;
		}

		for (let i = 0; i < count; i++) {
			const y = pos.getY(i);
			const t = (y - minY) / (maxY - minY);
			const v = THREE.MathUtils.lerp(0.8, -0.2, t);

			colors[i * 3 + 0] = v;
			colors[i * 3 + 1] = v;
			colors[i * 3 + 2] = v;
		}

		geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

		const material = new THREE.MeshBasicMaterial({
			color: 0xffffff,
			flatShading: false,
			roughness: 0,
			metalness: 0,
			vertexColors: true,
			toneMapped: true // IMPORTANT for UI-accurate hex colors in sRGB renderer
		});

		const instancedMesh = new THREE.InstancedMesh(geometry, material, instanceCount);
		instancedMesh.castShadow = true;
		instancedMesh.receiveShadow = false;
		instancedMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(instanceCount * 3), 3);

		const dummy = new THREE.Object3D();

		for (let i = 0; i < instanceCount; i++) {
			const item = markerItems[i];
			const spherical = UnitsUtils.datumsToSpherical(item.lat, item.lng);
			const pos = new THREE.Vector3(spherical.x, MARKER_POS_Y, -spherical.y);

			dummy.position.set(pos.x, pos.y, pos.z);
			dummy.rotation.set(Math.PI, 0, 0);
			dummy.updateMatrix();
			instancedMesh.setMatrixAt(i, dummy.matrix);

			item.pos = pos;

			item.index = i;
			this.pins[i] = item;
		}

		instancedMesh.instanceMatrix.needsUpdate = true;

		this.instancedMesh = instancedMesh;
		this.scene.add(instancedMesh);
		this.markersReady();
	}

	animateMarkers(time) {
		if (!this.instancedMesh || !this.pins.length) return;

		const matrix = new THREE.Matrix4();
		const position = new THREE.Vector3();
		const quaternion = new THREE.Quaternion();
		const scale = new THREE.Vector3();

		const state = appState.getState();
		const { id: currentId } = state;

		for (let i = 0; i < this.pins.length; i++) {
			const item = this.pins[i];

			this.instancedMesh.getMatrixAt(i, matrix);
			matrix.decompose(position, quaternion, scale);

			if (item.id === currentId) {
				const e = new THREE.Euler();
				e.setFromQuaternion(quaternion);

				const targetY = Math.PI * 0.5;
				e.y = THREE.MathUtils.lerp(e.y, targetY, 0.1);

				quaternion.setFromEuler(e);
			}

			matrix.compose(position, quaternion, scale);
			this.instancedMesh.setMatrixAt(i, matrix);
		}

		this.instancedMesh.instanceMatrix.needsUpdate = true;
	}

	updateBaseTransforms(distance) {
		if (!this.instancedMesh || !this.pins.length) return;

		const matrix = new THREE.Matrix4();
		const temp = new THREE.Object3D();

		const state = appState.getState();
		const dMin = 8000;
		const dMax = 654333;
		const t = THREE.MathUtils.clamp((distance - dMin) / (dMax - dMin), 0, 1);
		const baseScale = THREE.MathUtils.lerp(3, 15, t);

		for (let i = 0; i < this.pins.length; i++) {
			const item = this.pins[i];

			this.instancedMesh.getMatrixAt(i, matrix);
			matrix.decompose(temp.position, temp.quaternion, temp.scale);

			const isSelected = item.id === state.id;
			const isPresent = state.presentIds?.has(item.id);
			const isPast = state.pastIds?.has(item.id);
			const hasFilter = true;

			let targetScale = baseScale;

			if (isSelected) {
				targetScale = baseScale * 5;
			} else if (isPresent) {
				targetScale = baseScale * 1.4; // RED
			} else if (isPast) {
				targetScale = baseScale * 0.9; // BLACK
			} else {
				if (!state.visibleIds || state.visibleIds.size === 0) {
					targetScale = 0.00001;
				} else if (!state.visibleIds.has(item.id)) {
					targetScale = 0.00001;
				}
			}

			const prevScale = temp.scale.x;
			const newScale = THREE.MathUtils.lerp(prevScale, targetScale, 0.15);
			temp.scale.set(newScale, newScale, newScale);
			item.baseScale = newScale;

			let targetRotY = isPresent || isPast ? 0 : Math.PI / 4;

			const e = new THREE.Euler();
			e.setFromQuaternion(temp.quaternion);

			e.x = Math.PI;
			e.z = 0;
			e.y = THREE.MathUtils.lerp(e.y, targetRotY, 0.15);

			temp.quaternion.setFromEuler(e);

			temp.updateMatrix();
			this.instancedMesh.setMatrixAt(i, temp.matrix);
		}

		this.instancedMesh.instanceMatrix.needsUpdate = true;
	}

	toggle(state) {
		if (!this.instancedMesh || !this.pins.length) return;
		// console.log(state);
		const { presentIds, pastIds, id: currentId, hoverId } = state;
		const color = new THREE.Color();

		for (let i = 0; i < this.pins.length; i++) {
			const item = this.pins[i];
			if (!item) continue;

			// Selected
			if (item.id === currentId) {
				color.setHex(0xff3c18);
			} //hover
			else if (item.id === hoverId) {
				color.setHex(0xff3c18);
			}
			// Present
			else if (presentIds?.has(item.id)) {
				color.setHex(0xff3c18);
			}
			// Past
			else if (pastIds?.has(item.id)) {
				color.setHex(0x000000);
			}
			// White/default
			else {
				color.setHex(0xffffff);
			}
			this.instancedMesh.setColorAt(i, color);
		}

		this.instancedMesh.instanceColor.needsUpdate = true;
	}

	seededRandom(seed) {
		const x = Math.sin(seed) * 10000;
		return x - Math.floor(x);
	}

	onStateChange(state) {
		if (!this.instancedMesh) {
			this.setup(state.data || []);
			return;
		}
		this.toggle(state);
	}
}
